import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Building2, Palette, Upload, Users, Settings, User, ArrowLeft, Crown, MapPin, Mail } from 'lucide-react';
import EmailSignatureEditor from '@/components/organization/EmailSignatureEditor';
import { compressLogo } from '@/lib/imageUtils';
import DefaultTasksManagement from '@/components/DefaultTasksManagement';
import { useOrganizationTheme } from '@/hooks/useOrganizationTheme';
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { UserStatisticsDialog } from '@/components/UserStatisticsDialog';

import { AppSidebar } from "@/components/AppSidebar";
import { Link } from "react-router-dom";

interface Organization {
  id: string;
  name: string;
  color: string;
  logo_url: string | null;
  type: string;
  created_at: string;
}

interface TeamMember {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  user_roles: { role: string }[];
}

const OrganizationManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { organization: currentOrg, applyOrganizationTheme } = useOrganizationTheme();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>('');

  // Initialize sidebar state from localStorage, default to true (expanded)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);
  
  // Form states
  const [organizationColor, setOrganizationColor] = useState('#3B82F6');
  const [organizationLogo, setOrganizationLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    fetchOrganization();
    fetchTeamMembers();
  }, [user]);

  const fetchOrganization = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          organization_id,
          organizations (
            id,
            name,
            color,
            logo_url,
            type,
            created_at
          )
        `)
        .eq('user_id', user.id)
        .single();

      if (profileError) throw profileError;
      
      // Check if user has admin role
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      
      const hasAdminRole = roles?.some(r => r.role === 'admin') || false;
      
      if (!hasAdminRole) {
        toast.error('Sie haben keine Berechtigung, diese Seite zu besuchen.');
        return;
      }

      if (profile?.organizations) {
        const org = profile.organizations as any;
        setOrganization(org);
        setOrganizationColor(org.color || '#3B82F6');
        
        if (org.logo_url) {
          const { data } = supabase.storage
            .from('organization-logos')
            .getPublicUrl(org.logo_url);
          setLogoPreview(data.publicUrl);
        }
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      toast.error('Fehler beim Laden der Organisationsdaten');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    if (!user) return;

    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profile?.organization_id) return;

      // Fetch members
      const { data: members, error: membersError } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, created_at')
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: true });

      if (membersError) throw membersError;
      
      // Fetch roles for all members
      const { data: allRoles } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', members?.map(m => m.user_id) || []);
      
      // Combine members with their roles
      const membersWithRoles = members?.map(member => ({
        ...member,
        user_roles: allRoles?.filter(r => r.user_id === member.user_id).map(r => ({ role: r.role })) || []
      })) || [];
      
      setTeamMembers(membersWithRoles);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setOrganizationLogo(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleColorChange = (newColor: string) => {
    setOrganizationColor(newColor);
    
    // Apply theme immediately
    if (organization) {
      applyOrganizationTheme({
        id: organization.id,
        name: organization.name,
        color: newColor,
        type: organization.type
      });
    }
  };

  const handleSave = async () => {
    if (!organization) return;

    setSaving(true);
    try {
      let logoUrl = organization.logo_url;

      // Upload new logo if provided
      if (organizationLogo) {
        console.log('🚀 Starting logo upload process:', organizationLogo.name);
        
        // Delete old logo if exists
        if (organization.logo_url) {
          console.log('🗑️ Deleting old logo:', organization.logo_url);
          await supabase.storage
            .from('organization-logos')
            .remove([organization.logo_url]);
        }
        
        // Compress and resize logo before upload
        const compressedLogo = await compressLogo(organizationLogo);
        const fileExt = compressedLogo.name.split('.').pop();
        const fileName = `${organization.id}-logo-${Date.now()}.${fileExt}`;
        
        console.log('📤 Uploading compressed logo:', fileName);
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('organization-logos')
          .upload(fileName, compressedLogo);
        
        if (uploadError) {
          console.error('❌ Logo upload error:', uploadError);
          toast.error('Fehler beim Hochladen des Logos');
          return;
        } else {
          logoUrl = uploadData.path;
          console.log('✅ Logo uploaded successfully:', logoUrl);
        }
      }

      console.log('💾 Updating organization in database...');
      // Update organization
      const { error } = await supabase
        .from('organizations')
        .update({
          color: organizationColor,
          logo_url: logoUrl
        })
        .eq('id', organization.id);

      if (error) {
        console.error('❌ Organization update error:', error);
        throw error;
      }

      console.log('✅ Organization updated successfully');

      // Update local state
      setOrganization(prev => prev ? {
        ...prev,
        color: organizationColor,
        logo_url: logoUrl
      } : null);

      // Update logo preview with the new URL from storage with cache buster
      if (logoUrl) {
        const { data } = supabase.storage
          .from('organization-logos')
          .getPublicUrl(logoUrl);
        
        // Add cache buster to force reload
        const cacheBusterUrl = `${data.publicUrl}?v=${Date.now()}`;
        console.log('🖼️ New logo URL with cache buster:', cacheBusterUrl);
        setLogoPreview(cacheBusterUrl);
        
        // Force immediate update of the sidebar logo
        window.dispatchEvent(new CustomEvent('organizationLogoUpdated', {
          detail: { logoUrl: cacheBusterUrl }
        }));
      }

      // Reset logo file state
      setOrganizationLogo(null);

      console.log('🎉 Save process completed successfully!');
      toast.success('Organisationseinstellungen erfolgreich gespeichert!');
    } catch (error) {
      console.error('💥 Error saving organization:', error);
      toast.error('Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">Keine Organisation gefunden</h3>
            <p className="text-muted-foreground">
              Sie sind keiner Organisation zugeordnet oder haben keine Administratorrechte.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-full bg-gradient-subtle overflow-x-hidden">
        {/* Sidebar */}
        <AppSidebar />
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Top Bar */}
          <header className="h-12 sm:h-14 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
            <SidebarTrigger className="ml-2" />
            <div className="flex-1 flex items-center justify-between px-2 sm:px-4">
              <div>
                <h1 className="text-base sm:text-lg font-bold">Team verwalten</h1>
              </div>
              <div className="flex items-center gap-2 sm:gap-4">
                <Link to="/subscription">
                  <Button variant="outline" size="sm" className="gap-1 sm:gap-2">
                    <Crown className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Premium</span>
                  </Button>
                </Link>
              </div>
            </div>
          </header>

          {/* Content Area */}
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-full">
              <div className="flex items-center gap-3">
                <Building2 className="h-8 w-8" />
                <div>
                  <p className="text-muted-foreground">
                    Verwalten Sie die Einstellungen für "{organization.name}"
                  </p>
                </div>
              </div>

      <Tabs defaultValue="general" className="space-y-4 sm:space-y-6 w-full overflow-hidden">
        <TabsList className="grid w-full grid-cols-4 h-auto max-w-full">
          <TabsTrigger value="general" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-xs sm:text-sm p-2 sm:p-3 min-w-0">
            <Settings className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
            <span className="hidden xs:inline sm:inline truncate">Allgemein</span>
            <span className="xs:hidden text-[10px] truncate">Info</span>
          </TabsTrigger>
          <TabsTrigger value="tasks" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-xs sm:text-sm p-2 sm:p-3 min-w-0">
            <Users className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
            <span className="hidden xs:inline sm:inline truncate">Aufgaben</span>
            <span className="xs:hidden text-[10px] truncate">Tasks</span>
          </TabsTrigger>
          <TabsTrigger value="signature" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-xs sm:text-sm p-2 sm:p-3 min-w-0">
            <Mail className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
            <span className="hidden xs:inline sm:inline truncate">Signatur</span>
            <span className="xs:hidden text-[10px] truncate">E-Mail</span>
          </TabsTrigger>
          <TabsTrigger value="appearance" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-xs sm:text-sm p-2 sm:p-3 min-w-0">
            <Palette className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
            <span className="hidden xs:inline sm:inline truncate">Design</span>
            <span className="xs:hidden text-[10px] truncate">Style</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 sm:space-y-6 w-full overflow-hidden">
          <Card className="w-full overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl">Allgemeine Informationen</CardTitle>
              <CardDescription className="text-sm">
                Grundlegende Informationen über Ihre Organisation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-4 sm:p-6 w-full overflow-hidden">
              <div className="grid grid-cols-1 gap-4 w-full">
                {/* Logo Display */}
                <div className="space-y-2">
                  <Label>Organisationslogo</Label>
                  <div className="flex items-center justify-center w-24 h-24 border rounded-lg bg-muted">
                    {logoPreview ? (
                      <img 
                        src={logoPreview} 
                        alt={organization.name} 
                        className="w-full h-full object-contain rounded-lg"
                      />
                    ) : (
                      <Building2 className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  {!logoPreview && (
                    <p className="text-xs text-muted-foreground">
                      Kein Logo hochgeladen
                    </p>
                  )}
                </div>
                
                {/* Organization Name */}
                <div className="space-y-2">
                  <Label>Organisationsname</Label>
                  <Input 
                    value={organization.name} 
                    disabled 
                    className="bg-muted w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Der Name kann nicht geändert werden
                  </p>
                </div>
                
                {/* Organization Type */}
                <div className="space-y-2">
                  <Label>Typ</Label>
                  <div className="flex items-center gap-2 pt-2">
                    <Badge variant={organization.type === 'vodafone' ? 'default' : 'secondary'}>
                      {organization.type === 'vodafone' ? 'Vodafone' : 'Benutzerdefiniert'}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="space-y-2 w-full">
                <Label>Erstellt am</Label>
                <p className="text-sm text-muted-foreground break-words">
                  {new Date(organization.created_at).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Team Members */}
          <Card className="w-full overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                Team-Mitglieder
              </CardTitle>
              <CardDescription className="text-sm">
                Alle Mitglieder Ihres Teams und deren Beitrittsdatum
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 w-full overflow-hidden">
              {teamMembers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Keine Team-Mitglieder gefunden</p>
                </div>
              ) : (
                 <div className="space-y-3 w-full">
                   {teamMembers.map((member) => (
                     <div 
                       key={member.user_id} 
                       className="flex flex-col p-3 border rounded-lg gap-3 w-full overflow-hidden cursor-pointer hover:bg-muted/50 transition-colors"
                       onClick={() => {
                         setSelectedUserId(member.user_id);
                         setSelectedUserName(`${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Benutzer');
                       }}
                     >
                       <div className="flex items-center gap-3 w-full overflow-hidden">
                         <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                           <User className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                         </div>
                         <div className="min-w-0 flex-1 overflow-hidden">
                           <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2 w-full overflow-hidden">
                                <p className="font-medium text-sm sm:text-base truncate flex-1">
                                  {member.first_name || member.last_name 
                                    ? `${member.first_name || ''} ${member.last_name || ''}`.trim()
                                    : 'Unbekannter Benutzer'
                                  }
                                </p>
                                {member.user_roles?.some(r => r.role === 'admin') && (
                                  <Badge variant="default" className="text-xs flex-shrink-0">
                                    Teamleader
                                  </Badge>
                                )}
                                <Badge variant="outline" className="text-xs flex-shrink-0">
                                  {member.user_roles?.some(r => r.role === 'admin') ? 'Admin' : 'Mitglied'}
                                </Badge>
                              </div>
                             <p className="text-xs sm:text-sm text-muted-foreground truncate">
                               Beigetreten am {new Date(member.created_at).toLocaleDateString('de-DE', {
                                 year: 'numeric',
                                 month: 'long',
                                 day: 'numeric'
                               })}
                             </p>
                           </div>
                         </div>
                       </div>
                     </div>
                   ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4 sm:space-y-6">
          <DefaultTasksManagement />
        </TabsContent>

        <TabsContent value="signature" className="space-y-4 sm:space-y-6">
          <EmailSignatureEditor 
            organizationId={organization.id}
            organizationName={organization.name}
            logoUrl={organization.logo_url}
          />
        </TabsContent>

        <TabsContent value="appearance" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl">Erscheinungsbild anpassen</CardTitle>
              <CardDescription className="text-sm">
                Passen Sie Farben und Logo Ihrer Organisation an
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
              <div className="space-y-4">
                <div className="space-y-3">
                  <Label htmlFor="orgColor">Organisationsfarbe</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="orgColor"
                      type="color"
                      value={organizationColor}
                      onChange={(e) => handleColorChange(e.target.value)}
                      className="w-16 h-10 p-1 rounded cursor-pointer"
                    />
                    <div className="flex-1">
                      <Input
                        value={organizationColor}
                        onChange={(e) => handleColorChange(e.target.value)}
                        placeholder="#3B82F6"
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Diese Farbe wird für das gesamte Theme verwendet und sofort angewendet
                  </p>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="orgLogo">Organisationslogo</Label>
                  <div className="flex items-start gap-4">
                    <div className="space-y-2">
                      <Input
                        id="orgLogo"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="cursor-pointer"
                      />
                      {organizationLogo && (
                        <div className="flex items-center gap-2 text-sm text-green-600">
                          <span>✓</span>
                          <span>{organizationLogo.name}</span>
                        </div>
                      )}
                    </div>
                    {logoPreview && (
                      <div className="flex flex-col items-center space-y-2">
                        <img 
                          src={logoPreview} 
                          alt="Logo Vorschau" 
                          className="w-16 h-16 object-contain rounded border"
                        />
                        <span className="text-xs text-muted-foreground">
                          {organizationLogo ? 'Vorschau' : 'Aktuell'}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Laden Sie ein neues Logo hoch (PNG, JPG, etc.)
                  </p>
                </div>

                <div className="pt-4">
                  <Button 
                    onClick={handleSave} 
                    disabled={isSaving}
                    className="gap-2"
                  >
                    {isSaving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {isSaving ? 'Speichere...' : 'Änderungen speichern'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
            </div>
          </div>
        </div>
      </div>
      
      {/* User Statistics Dialog */}
      <UserStatisticsDialog
        isOpen={selectedUserId !== null}
        onClose={() => {
          setSelectedUserId(null);
          setSelectedUserName('');
        }}
        userId={selectedUserId || ''}
        userName={selectedUserName}
      />
    </SidebarProvider>
  );
};

export default OrganizationManagement;